import { useState } from "react"
import { Box, Button, Link, Menu, MenuItem, Popover, Stack, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { SelectionRuleItem } from "./selection-rule-item"
import { warningTextColor } from "./warning-text-color"

/**
 * Pinned to the bottom of the schedule list while anything is ticked, so a selection made
 * far down a long list can be assigned without scrolling or dragging back up to a trade.
 * Says what the assignment would cover and which conditional skill rules its work would
 * ignore, beside the action rather than in a confirmation.
 */
export function SelectionActionBar() {
    const { board, selectedCount, takeoverCount, selectionScope, onAssignSelection, onClearSelection } =
        useBoundContext()
    const [menuAnchor, setMenuAnchor] = useState(null)
    // The rule names open upward from the link: the bar sits at the bottom of the screen, so a
    // list expanding below it would fall off-screen
    const [rulesAnchor, setRulesAnchor] = useState(null)
    const rules = selectionScope?.rules ?? []
    const tradeItems = tradeMenuItems()

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            data-cy="assignment-selection-bar"
            sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 1,
                px: 2,
                py: 1,
                bgcolor: "background.paper",
                borderTop: 1,
                borderColor: "divider",
                boxShadow: 4,
            }}
        >
            <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" data-cy="assignment-selected-count">
                    {selectedCount} selected
                </Typography>
                <Typography variant="caption" data-cy="assignment-selection-scope">
                    {selectionScope?.scope}
                </Typography>
                <If value={rules.length > 0}>
                    <Typography variant="caption" sx={{ color: warningTextColor }} data-cy="assignment-selection-rules">
                        {selectionScope?.rulesText}{" "}
                        <Link
                            component="button"
                            variant="caption"
                            onClick={openRules}
                            aria-expanded={Boolean(rulesAnchor)}
                            aria-haspopup="true"
                            data-cy="assignment-selection-rules-toggle"
                        >
                            Show rules
                        </Link>
                    </Typography>
                    <Popover
                        open={Boolean(rulesAnchor)}
                        anchorEl={rulesAnchor}
                        onClose={closeRules}
                        anchorOrigin={{ vertical: "top", horizontal: "left" }}
                        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                    >
                        <Box sx={{ p: 1.5, maxWidth: 360 }} data-cy="assignment-selection-rule-list">
                            <Typography variant="caption" color="text.secondary">
                                Assigned work ignores these rules:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                                <Repeat list={rules} keyFn={ruleKey} pass="rule" item={<SelectionRuleItem />} />
                            </Box>
                        </Box>
                    </Popover>
                </If>
                <If value={takeoverCount > 0}>
                    <Typography variant="caption" sx={{ color: warningTextColor }} data-cy="assignment-takeover-count">
                        {describeTakeovers(takeoverCount)}
                    </Typography>
                </If>
            </Stack>
            <Button size="small" onClick={onClearSelection} data-cy="assignment-clear-selection">
                Clear
            </Button>
            <Button
                size="small"
                variant="contained"
                endIcon={<Iconify icon="eva:chevron-up-fill" />}
                onClick={openMenu}
                data-cy="assignment-assign-to"
            >
                Assign to…
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                {tradeItems}
            </Menu>
        </Stack>
    )

    // Anchored to the top of the bar, so the list sits above it without covering what it says
    function openRules(event) {
        setRulesAnchor(event.currentTarget.closest("[data-cy='assignment-selection-bar']") ?? event.currentTarget)
    }

    function closeRules() {
        setRulesAnchor(null)
    }

    function openMenu(event) {
        setMenuAnchor(event.currentTarget)
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    // MenuItems stay direct Menu children (an array, not <Repeat>) so MUI wires up each one.
    // With no trades yet the menu says so rather than opening empty.
    function tradeMenuItems() {
        if (!board.trades.length) {
            return [
                <MenuItem key="none" disabled data-cy="assignment-assign-to-no-trades">
                    No trades yet
                </MenuItem>,
            ]
        }
        return board.trades.map(toMenuItem)
    }

    function toMenuItem(trade) {
        return (
            <MenuItem key={trade.tradeId} onClick={assignTo(trade.tradeId)} data-cy="assignment-assign-to-trade">
                {trade.tradeName}
            </MenuItem>
        )
    }

    function assignTo(tradeId) {
        return function handleAssign() {
            closeMenu()
            onAssignSelection(tradeId)
        }
    }
}

function ruleKey(rule) {
    return rule.name
}

// Schedules filed in a ticked folder and in another folder that is already assigned: the
// ticked folder wins for them, so they leave their current trade (see folderTakeovers)
function describeTakeovers(count) {
    return `Also moves ${count} ${"schedule".pluralize(count)} from another folder's trade`
}
